import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { DateService } from '@application/time/date.service';
import { UniversalConfig } from '@application/universal_config.service';
import { useTimeSlots } from '@feature/time_slot/use_time_slots';
import type {
	CurrencyDisplay,
	PhysicalLocationApiModel,
	ResourceModel,
	SearchCapabilitiesApiModel,
	TimeslotApiModel,
} from '@visiba/api-vcs-client-v2';
import { LocalityType } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { safeNumberParse } from '@visiba/std';
import { Dayjs } from 'dayjs';
import { createContext, type PropsWithChildren, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useBookingBasket } from '../../hooks/use_booking_basket';

// Helper: useState + ref for reading current value in async methods
function useStateWithRef<T>(initial: T): [T, React.MutableRefObject<T>, (value: T) => void] {
	const [state, setState] = useState(initial);
	const ref = useRef(initial);
	const set = (value: T) => {
		ref.current = value;
		setState(value);
	};
	return [state, ref, set];
}

interface SelectedFilters {
	resourceId: number | null;
	specialityId: number | null;
	unitId: number | null;
}

interface Filters {
	resources: SearchCapabilitiesApiModel['Resources'];
	specialities: SearchCapabilitiesApiModel['Specialities'];
	units: SearchCapabilitiesApiModel['FilterableUnits'];
}

interface DateSpan {
	from: Nullable<Dayjs>;
	to: Nullable<Dayjs>;
}

interface AsyncState<T> {
	value: T | null;
	status: 'idle' | 'pending' | 'accessible' | 'error';
}

export interface TimeSlotDetails {
	duration: number;
	id: string;
	description: string | null;
	price: CurrencyDisplay | null;
	requiresPayment: boolean;
	resource: ResourceModel | null;
	start: string;
	unbookableAfter: string;
	physicalLocation: PhysicalLocationApiModel | null;
}

type Duration = {
	hours: number;
	minutes: number;
	seconds: number;
};

export interface ScreenTimeSlotContextValue {
	location: Nullable<PhysicalLocationApiModel>;
	currentDate: Nullable<Dayjs>;
	dateSpan: DateSpan;
	availability: { dates: Map<string, number>; next: Nullable<Dayjs> };
	nextAvailableDate: Nullable<Dayjs>;
	timeSlots: AsyncState<TimeslotApiModel[]>;
	timeSlotDetails: AsyncState<TimeSlotDetails>;
	filters: AsyncState<Filters>;
	selectedFilters: SelectedFilters;
	showUnavailableTimeSlotDialog: boolean;
	setShowUnavailableTimeSlotDialog: (value: boolean) => void;
	dateService: DateService;
	setup(localityType: LocalityType, locationId?: string): Promise<void>;
	setCurrentDate(date: Dayjs, forceUpdate?: boolean): Promise<void>;
	setFilter(filters: SelectedFilters): void;
	previewTimeSlot(timeSlotId: string): Promise<unknown>;
	clearTimeSlotDetails(): Promise<void>;
	clearSelectTimeSlot(): void;
	selectTimeSlot(timeSlotId: string, redirect?: boolean): Promise<void>;
}

const ScreenTimeSlotContext = createContext<ScreenTimeSlotContextValue | null>(null);

export function useScreenTimeSlot(): ScreenTimeSlotContextValue {
	const context = useContext(ScreenTimeSlotContext);
	if (context == null) throw new Error('useScreenTimeSlot must be used within a <ScreenTimeSlotProvider>');
	return context;
}

export function ScreenTimeSlotProvider({ children }: PropsWithChildren) {
	const viewBooking = useBooking();
	const analyticService = useResolve(AnalyticService);
	const bookingBasket = useBookingBasket();
	const timeSlotsApi = useTimeSlots();
	const universalConfig = useResolve(UniversalConfig);
	const dateService = useResolve(DateService);

	// State (with refs for async method access where needed)
	const [location, locationRef, setLocation] = useStateWithRef<Nullable<PhysicalLocationApiModel>>(null);
	const [currentDate, currentDateRef, setCurrentDateValue] = useStateWithRef<Nullable<Dayjs>>(null);
	const [dateSpan, dateSpanRef, setDateSpan] = useStateWithRef<DateSpan>({ from: null, to: null });
	const [availability, availabilityRef, setAvailability] = useStateWithRef<{ dates: Map<string, number>; next: Nullable<Dayjs> }>({
		dates: new Map(),
		next: null,
	});
	const [nextAvailableDate, , setNextAvailableDateValue] = useStateWithRef<Nullable<Dayjs>>(null);
	const [timeSlots, timeSlotsRef, setTimeSlots] = useStateWithRef<AsyncState<TimeslotApiModel[]>>({
		value: null,
		status: 'pending',
	});
	const [timeSlotDetails, , setTimeSlotDetails] = useStateWithRef<AsyncState<TimeSlotDetails>>({ value: null, status: 'idle' });
	const [filters, , setFilters] = useStateWithRef<AsyncState<Filters>>({ value: null, status: 'idle' });
	const [selectedFilters, selectedFiltersRef, setSelectedFilters] = useStateWithRef<SelectedFilters>({
		resourceId: null,
		specialityId: null,
		unitId: null,
	});
	const [showUnavailableTimeSlotDialog, , setShowUnavailableTimeSlotDialog] = useStateWithRef(false);
	const localityTypeRef = useRef<LocalityType>(LocalityType.Digital);

	// --- Internal helpers ---

	function addDurationToDate(duration: Duration | string): Dayjs {
		let date = dateService.newDate();
		function parseDurationFormat(value: string): Duration {
			const splittedValue = value.split(':').reverse();
			return {
				seconds: safeNumberParse(splittedValue[0]) ?? 0,
				minutes: safeNumberParse(splittedValue[1]) ?? 0,
				hours: safeNumberParse(splittedValue[2]) ?? 0,
			};
		}
		if (typeof duration === 'string') {
			duration = parseDurationFormat(duration);
		}
		date = date.add(duration.hours, 'hours').add(duration.minutes, 'minutes').add(duration.seconds, 'seconds');
		return date;
	}

	function populateDateSpan() {
		const flowSettings = viewBooking.getFlowSettings();
		let newDateSpan: DateSpan = { from: null, to: null };

		if (flowSettings.searchContext) {
			const searchContext = flowSettings.searchContext;
			if (searchContext.FirstSearchDate != null) {
				newDateSpan.from = dateService.newDate(searchContext.FirstSearchDate);
			}
			if (searchContext.LastSearchDate != null) {
				newDateSpan.to = dateService.newDate(searchContext.LastSearchDate);
			}
			if (searchContext.RelativeStart != null) {
				const fromDate = addDurationToDate(searchContext.RelativeStart);
				if (newDateSpan.from == null || newDateSpan.from.isBefore(fromDate, 'day')) {
					newDateSpan.from = fromDate;
				}
			}
			if (searchContext.RelativeEnd != null) {
				const toDate = addDurationToDate(searchContext.RelativeEnd);
				if (newDateSpan.to == null || newDateSpan.to.isAfter(toDate, 'day')) {
					newDateSpan.to = toDate;
				}
			}
		}

		if (flowSettings.fromDate && (newDateSpan.from == null || newDateSpan.from.isBefore(flowSettings.fromDate, 'day'))) {
			newDateSpan.from = flowSettings.fromDate;
		}
		if (flowSettings.toDate && (newDateSpan.to == null || newDateSpan.to.isAfter(flowSettings.toDate, 'day'))) {
			newDateSpan.to = flowSettings.toDate;
		}

		setDateSpan(newDateSpan);
	}

	const allowedToBeHere = useCallback(() => {
		if (viewBooking.flowData.status !== 'accessible') return false;
		const flowData = viewBooking.flowData.value;
		const basket = bookingBasket.getBasket();
		if (basket.rescheduleTicket) return true;
		if (basket.respondantType == null) return false;
		if (
			flowData.useReasonForVisits &&
			viewBooking.getFilteredReasonForVisits().length > 0 &&
			!viewBooking.getSelectedReasonForVisit()
		) {
			return false;
		}
		return true;
	}, [viewBooking, bookingBasket.getBasket]);

	async function getAvailability() {
		const flowSettings = viewBooking.getFlowSettings();
		const basket = bookingBasket.getBasket();

		const date = currentDateRef.current;
		if (!date) return Err('No current date');

		const span = dateSpanRef.current;
		let startDate = date.startOf('week');
		if (span.from && startDate.isBefore(span.from, 'day')) startDate = span.from;
		let endDate = startDate.endOf('week');
		if (span.to && endDate.isAfter(span.to, 'day')) endDate = span.to;

		const currentFilters = selectedFiltersRef.current;

		let availabilityCounts;
		try {
			availabilityCounts = basket.rescheduleTicket
				? await timeSlotsApi.getRescheduleAvailabilityCounts(universalConfig.selectedUnitId, basket.rescheduleTicket, {
						fromDate: startDate.format('YYYY-MM-DD'),
						toDate: endDate.format('YYYY-MM-DD'),
						fromTime: '00:00:00',
						toTime: '23:59:59',
					})
				: await timeSlotsApi.getAvailabilityCounts(universalConfig.selectedUnitId, {
						searchContextId: flowSettings.searchContextId ?? undefined,
						conditionId: basket.reasonForVisit ?? undefined,
						fromDate: startDate.format('YYYY-MM-DD'),
						toDate: endDate.format('YYYY-MM-DD'),
						fromTime: '00:00:00',
						toTime: '23:59:59',
						localityType: localityTypeRef.current,
						physicalLocationId: locationRef.current?.Id,
						resourceId: currentFilters.resourceId ?? undefined,
						specialityId: currentFilters.specialityId ?? undefined,
						filterableUnitId: currentFilters.unitId ?? undefined,
						additionalParameters: flowSettings.additionalParameters ?? undefined,
					});
		} catch (error) {
			return Err(error);
		}

		const yesterday = dateService.newDate().subtract(1, 'day');
		const dates = new Map<string, number>();
		availabilityCounts.AvailableDates.filter((a) => yesterday.isBefore(a.Date, 'day')).map((a) => {
			dates.set(a.Date, a.Count);
		});

		let nextAvailable = availabilityCounts.NextAvailableDate
			? dateService.newDate(availabilityCounts.NextAvailableDate, 'YYYY-MM-DD')
			: null;
		if (nextAvailable && ((span.to && nextAvailable.isAfter(span.to, 'day')) || nextAvailable.isSame(date, 'day'))) {
			nextAvailable = null;
		}

		setAvailability({ dates, next: nextAvailable });
		return Ok();
	}

	function computeNextAvailableDate() {
		const date = currentDateRef.current;
		if (!date) return;

		let next: Nullable<Dayjs> = null;
		for (const [dateStr, count] of availabilityRef.current.dates) {
			const parsedDate = dateService.newDate(dateStr, 'YYYY-MM-DD');
			if (parsedDate.isAfter(date, 'day') && count > 0) {
				next = parsedDate;
				break;
			}
		}
		if (!next) {
			const upcoming = availabilityRef.current.next;
			next = upcoming?.isAfter(date, 'day') ? upcoming : null;
		}
		setNextAvailableDateValue(next);
	}

	async function loadTimeSlots() {
		const flowSettings = viewBooking.getFlowSettings();
		const basket = bookingBasket.getBasket();

		const date = currentDateRef.current;
		if (!date) return Err('No current date');

		setTimeSlots({ value: null, status: 'pending' });

		const currentFilters = selectedFiltersRef.current;

		try {
			const data = basket.rescheduleTicket
				? await timeSlotsApi.getRescheduleTimeSlots(universalConfig.selectedUnitId, basket.rescheduleTicket, {
						fromDate: date.format('YYYY-MM-DD'),
						toDate: date.format('YYYY-MM-DD'),
						fromTime: '00:00:00',
						toTime: '23:59:59',
					})
				: await timeSlotsApi.getTimeSlots(universalConfig.selectedUnitId, {
						searchContextId: flowSettings.searchContextId ?? undefined,
						conditionId: basket.reasonForVisit ?? undefined,
						fromDate: date.format('YYYY-MM-DD'),
						toDate: date.format('YYYY-MM-DD'),
						fromTime: '00:00:00',
						toTime: '23:59:59',
						localityType: localityTypeRef.current,
						physicalLocationId: locationRef.current?.Id,
						resourceId: currentFilters.resourceId ?? undefined,
						specialityId: currentFilters.specialityId ?? undefined,
						filterableUnitId: currentFilters.unitId ?? undefined,
						additionalParameters: flowSettings.additionalParameters ?? undefined,
					});

			setTimeSlots({ value: data, status: 'accessible' });
			return Ok();
		} catch (error) {
			setTimeSlots({ value: null, status: 'error' });
			return Err(error);
		}
	}

	async function fetchFilters() {
		setFilters({ value: null, status: 'pending' });

		try {
			const capabilities = await timeSlotsApi.getSearchCapabilities(universalConfig.selectedUnitId);

			const flowSettings = viewBooking.getFlowSettings();
			setFilters({
				value: {
					resources: flowSettings.lockFilters
						? (capabilities.Resources?.filter((resource) => resource.Id === flowSettings.filterResourceId) ?? null)
						: capabilities.Resources,
					specialities: flowSettings.lockFilters
						? (capabilities.Specialities?.filter((speciality) => speciality.Id === flowSettings.filterSpecialityId) ?? null)
						: capabilities.Specialities,
					units: flowSettings.lockFilters
						? (capabilities.FilterableUnits?.filter((unit) => unit.Id === flowSettings.filterUnitId) ?? null)
						: capabilities.FilterableUnits,
				},
				status: 'accessible',
			});
		} catch {
			setFilters({ value: null, status: 'error' });
		}
	}

	async function getTimeSlot(timeSlotId: string): Promise<TimeslotApiModel> {
		const cached = timeSlotsRef.current.value?.find((ts) => ts.Id === timeSlotId);
		if (cached) return cached;

		return timeSlotsApi.getTimeSlot(universalConfig.selectedUnitId, timeSlotId);
	}

	// --- Public methods ---

	async function setup(localityType: LocalityType, locationId?: string) {
		populateDateSpan();

		localityTypeRef.current = localityType;
		setLocation(
			(viewBooking.flowData.status === 'accessible'
				? viewBooking.flowData.value.physicalLocations.find((location) => location.Id.toString() == locationId)
				: null) ?? null,
		);

		fetchFilters();

		const basketData = bookingBasket.getBasket();
		const date = basketData.currentDate ?? undefined;
		setCurrentDateValue(date ?? dateService.newDate());

		const availabilityResult = await getAvailability();
		if (availabilityResult.err) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingAvailability);
			viewBooking.goToScreen(BookingScreen.Error);
			return;
		}

		const avail = availabilityRef.current;
		let shouldFetchTimeSlots = false;

		if (date != null) {
			if (avail.dates.has(date.format('YYYY-MM-DD'))) {
				shouldFetchTimeSlots = true;
			}
		} else {
			if (avail.dates.size === 0 && avail.next != null) {
				await setCurrentDate(avail.next);
				return;
			}
			if (avail.dates.size > 0) {
				const firstAvailableDate = avail.dates.entries().next().value?.[0] ?? undefined;
				setCurrentDateValue(dateService.newDate(firstAvailableDate, 'YYYY-MM-DD'));
				shouldFetchTimeSlots = true;
			}
		}

		if (shouldFetchTimeSlots) {
			const timeSlotResult = await loadTimeSlots();
			if (timeSlotResult.err) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlots);
				viewBooking.goToScreen(BookingScreen.Error);
			}
		} else {
			setTimeSlots({ value: [], status: 'accessible' });
		}

		computeNextAvailableDate();
	}

	async function setCurrentDate(date: Dayjs, forceUpdate = false) {
		const current = currentDateRef.current;

		if (current && date.isSame(current, 'day') && !forceUpdate) return;

		const isNewWeek = current == null || !current.isSame(date, 'week') || forceUpdate;
		setCurrentDateValue(date);

		if (isNewWeek) {
			const availabilityResult = await getAvailability();
			if (availabilityResult.err) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingAvailability);
				viewBooking.goToScreen(BookingScreen.Error);
				return;
			}
		}

		if (availabilityRef.current.dates.has(date.format('YYYY-MM-DD'))) {
			const timeslotResult = await loadTimeSlots();
			if (timeslotResult.err) {
				analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlots);
				viewBooking.goToScreen(BookingScreen.Error);
				return;
			}
		} else {
			setTimeSlots({ value: [], status: 'accessible' });
		}

		bookingBasket.setBasket({ currentDate: date.format('YYYY-MM-DD') });
		computeNextAvailableDate();
	}

	function setFilter(newFilters: SelectedFilters) {
		setSelectedFilters(newFilters);
		bookingBasket.setBasket({
			timeSlotFilterResourceId: newFilters.resourceId,
			timeSlotFilterSpecialityId: newFilters.specialityId,
			timeSlotFilterUnitId: newFilters.unitId,
		});
		setCurrentDate(currentDateRef.current ?? dateService.newDate(), true);
	}

	async function previewTimeSlot(timeSlotId: string) {
		let selectedTimeSlot: TimeslotApiModel;
		try {
			selectedTimeSlot = await getTimeSlot(timeSlotId);
		} catch (error) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlotDetails);
			return Err(error);
		}

		const lockResult = await bookingBasket.lockTimeSlot(timeSlotId);
		if (lockResult.err) {
			loadTimeSlots();
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorWhileLockingTimeSlot);
			setShowUnavailableTimeSlotDialog(true);
			clearSelectTimeSlot();
			return Err(lockResult.err);
		}

		let physicalLocation: PhysicalLocationApiModel | null = null;
		if (selectedTimeSlot.PhysicalLocationId) {
			physicalLocation =
				(viewBooking.flowData.status === 'accessible'
					? viewBooking.flowData.value.physicalLocations.find((l) => l.Id === selectedTimeSlot.PhysicalLocationId)
					: null) ?? null;
		}

		setTimeSlotDetails({
			status: 'accessible',
			value: {
				physicalLocation,
				requiresPayment: selectedTimeSlot.RequiresPayment,
				duration: selectedTimeSlot.Duration,
				id: selectedTimeSlot.Id ?? 'NO_ID',
				description: selectedTimeSlot.Name,
				price: selectedTimeSlot.Price,
				resource: selectedTimeSlot.Resource,
				start: selectedTimeSlot.Start,
				unbookableAfter: selectedTimeSlot.UnbookableAfter,
			},
		});

		return Ok();
	}

	async function clearTimeSlotDetails() {
		setTimeSlotDetails({ status: 'idle', value: null });
		clearSelectTimeSlot();
		await bookingBasket.unlockTimeSlot();
	}

	function clearSelectTimeSlot() {
		bookingBasket.setBasket({ timeSlotId: null });
	}

	async function selectTimeSlot(timeSlotId: string, redirect = true) {
		const basket = bookingBasket.getBasket();
		await bookingBasket.setBasket({ timeSlotId });
		if (redirect) {
			if (basket.rescheduleTicket) {
				viewBooking.goToScreen(BookingScreen.Conclusion);
			} else {
				viewBooking.goToScreen(BookingScreen.Forms);
			}
		}
	}

	// Mount effect
	// biome-ignore lint/correctness/useExhaustiveDependencies: setSelectedFilters is intentionally omitted because it is recreated per render by useStateWithRef; allowedToBeHere reads current hook state for this mount guard.
	useEffect(() => {
		analyticService.trackPageView({ customTitle: 'Booking: Time slot' });

		if (!allowedToBeHere()) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Time slot');
			viewBooking.goToScreen(BookingScreen.Location, { replace: true });
			return;
		}

		const { timeSlotFilterResourceId, timeSlotFilterSpecialityId, timeSlotFilterUnitId } = bookingBasket.getBasket();
		const flowSettings = viewBooking.getFlowSettings();
		setSelectedFilters({
			resourceId: flowSettings.filterResourceId ?? timeSlotFilterResourceId,
			specialityId: flowSettings.filterSpecialityId ?? timeSlotFilterSpecialityId,
			unitId: flowSettings.filterUnitId ?? timeSlotFilterUnitId,
		});
	}, [analyticService, bookingBasket.getBasket, viewBooking.goToScreen, viewBooking.getFlowSettings]);

	const value: ScreenTimeSlotContextValue = {
		location,
		currentDate,
		dateSpan,
		availability,
		nextAvailableDate,
		timeSlots,
		timeSlotDetails,
		filters,
		selectedFilters,
		showUnavailableTimeSlotDialog,
		setShowUnavailableTimeSlotDialog,
		dateService,
		setup,
		setCurrentDate,
		setFilter,
		previewTimeSlot,
		clearTimeSlotDetails,
		clearSelectTimeSlot,
		selectTimeSlot,
	};

	return <ScreenTimeSlotContext.Provider value={value}>{children}</ScreenTimeSlotContext.Provider>;
}
